import type { NodeColumns, TableRow } from './table-store';

/** String-literal union of every node column key (NodeColumns is a string enum),
 *  usable without importing the heavy store at runtime. */
export type NodeColumnKey = `${NodeColumns}`;

/** Bumped only on a breaking envelope shape change — parsers reject other versions. */
export const NODE_CLIPBOARD_VERSION = 1;
/** data-* attribute on the copied <table> carrying the full-fidelity JSON. */
export const NODE_CLIPBOARD_HTML_ATTR = 'data-pypestream-nodes';

export interface NodeClipboardEnvelope {
  version: number;
  isCut: boolean;
  /** Provenance — lets a consumer distinguish same- from cross-solution. */
  sourceSolutionVersionId?: string;
  /** Full-fidelity rows (all columns + group settings/subRows). */
  rows: TableRow[];
}

/** Serialize a node bundle to the canonical JSON envelope string. */
export function serializeNodeEnvelope(
  rows: TableRow[],
  opts: { isCut: boolean; sourceSolutionVersionId?: string }
): string {
  const envelope: NodeClipboardEnvelope = {
    version: NODE_CLIPBOARD_VERSION,
    isCut: opts.isCut,
    sourceSolutionVersionId: opts.sourceSolutionVersionId,
    rows,
  };
  return JSON.stringify(envelope);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Build the text/html clipboard representation: a real <table> (Google Sheets &
 * other rich targets render clean columns) whose root carries the full-fidelity
 * JSON envelope in a data-* attribute (Pro Studio rebuilds the exact nodes — the
 * visible cells alone drop group structure and JSON columns). The JSON is
 * `encodeURIComponent`-encoded so it contains no quote/`<`/`>`/space and is safe
 * inside the double-quoted attribute.
 */
export function buildNodeClipboardHtml(
  tsvGrid: string[][],
  envelopeJson: string
): string {
  const encoded = encodeURIComponent(envelopeJson);
  const body = tsvGrid
    .map(
      (r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`
    )
    .join('');
  return `<table ${NODE_CLIPBOARD_HTML_ATTR}="${encoded}"><tbody>${body}</tbody></table>`;
}

function decodeEnvelope(getJson: () => string): NodeClipboardEnvelope | null {
  try {
    const parsed = JSON.parse(getJson()) as Partial<NodeClipboardEnvelope>;
    if (
      !parsed ||
      parsed.version !== NODE_CLIPBOARD_VERSION ||
      !Array.isArray(parsed.rows)
    ) {
      return null;
    }
    return {
      version: parsed.version,
      isCut: !!parsed.isCut,
      sourceSolutionVersionId: parsed.sourceSolutionVersionId,
      rows: parsed.rows as TableRow[],
    };
  } catch {
    return null;
  }
}

/** Extract our envelope from a text/html payload. Returns null for foreign or
 *  malformed html (e.g. a Google Sheets copy → no `data-pypestream-nodes`). */
export function parseNodeEnvelopeFromHtml(
  html: string | null | undefined
): NodeClipboardEnvelope | null {
  if (!html) return null;
  // Require a `<` or whitespace before the attribute so a foreign attribute whose
  // name merely ENDS with our slug (e.g. `x-data-pypestream-nodes="…"`) can't
  // false-match. Our own carrier always emits `<table data-pypestream-nodes=`.
  const match = html.match(
    new RegExp(`[\\s<]${NODE_CLIPBOARD_HTML_ATTR}="([^"]*)"`)
  );
  if (!match) return null;
  return decodeEnvelope(() => decodeURIComponent(match[1]));
}

/** Fallback for targets exposing only text/plain that contain our JSON directly. */
export function parseNodeEnvelopeFromText(
  text: string | null | undefined
): NodeClipboardEnvelope | null {
  if (!text) return null;
  return decodeEnvelope(() => text);
}

/** Columns exported to the flat TSV view (Google-Sheets-facing). Mirrors the
 *  editor's visible column order minus the internal `id`; group structure and
 *  JSON columns still round-trip fully via the HTML data-attr envelope. */
export const TSV_EXPORT_COLUMNS: NodeColumnKey[] = [
  'nodeNumber',
  'nodeType',
  'nodeName',
  'intent',
  'utterances',
  'nluDisabled',
  'nextNodes',
  'message',
  'richAssetType',
  'richAssetContent',
  'behaviors',
  'answerRequired',
  'command',
  'description',
  'output',
  'nodeInput',
  'parameterInput',
  'decisionVariable',
  'whatsNext',
  'nodeTags',
  'skillTag',
  'variables',
  'platformFlag',
  'cssClassname',
  'analyticsEvent',
];

/**
 * Column headers used for the TSV header row (PS → Google Sheets export) and to
 * recognise a Google-Sheets paste's header line on import. These MUST mirror the
 * editor `<th>` labels in `table.tsx` (the `header:` strings) so a user who copies
 * a header row straight from the live grid is recognised on paste, and an exported
 * bundle re-imports cleanly. Keep in sync when a column header changes there.
 * (`intent` has no standalone editor `<th>` — its label is our own.)
 */
export const COLUMN_DISPLAY_NAMES: Partial<Record<NodeColumnKey, string>> = {
  nodeNumber: 'Node #',
  nodeType: 'Node Type',
  nodeName: 'Node Name',
  intent: 'Intent',
  utterances: 'Example Phrases',
  nluDisabled: 'NLU Disabled?',
  nextNodes: 'Next Nodes',
  message: 'Message',
  richAssetType: 'Asset Type',
  richAssetContent: 'Asset Content',
  behaviors: 'Behaviors',
  answerRequired: 'Ans Req?',
  command: 'Command',
  description: 'Description',
  output: 'Output',
  nodeInput: 'Node Input',
  parameterInput: 'Parameter Input',
  decisionVariable: 'Decision Variable',
  whatsNext: 'What Next?',
  nodeTags: 'Node Tags',
  skillTag: 'Skill Tag',
  variables: 'Variables',
  platformFlag: 'Platform Flag',
  cssClassname: 'CSS Classname',
  analyticsEvent: 'Analytics Events',
};

export interface TsvParseResult {
  /** Resolved column key per TSV column index (undefined = unmapped/ignored). */
  columns: Array<NodeColumnKey | undefined>;
  cellMaps: Array<Partial<Record<NodeColumnKey, string>>>;
}

/**
 * Flatten a grid to TSV (TAB between cells, NEWLINE between rows). This is a
 * LOSSY, flat view: a cell value that itself contains a TAB or NEWLINE (e.g. a
 * multi-line `message`, or a `parameterInput` JSON with a literal newline) will
 * shift columns / split rows on a plain-TSV re-parse. That is acceptable because
 * the flat TSV is only the `text/plain` fallback: Pro Studio ↔ Pro Studio uses
 * the full-fidelity JSON envelope (in `text/html`), and Google Sheets consumes
 * the `text/html` `<table>` (one cell per `<td>`, so embedded newlines stay
 * inside their cell). Single-line cells — the common case — round-trip cleanly.
 */
export function gridToTsv(grid: string[][]): string {
  return grid.map((r) => r.join('\t')).join('\n');
}

/**
 * RFC-4180-style TSV tokenizer (PSTUDIO-3611). Google Sheets' text/plain wraps
 * any cell containing a newline / tab / double quote in double quotes and
 * escapes embedded quotes as `""`. The previous naive newline split exploded
 * one logical row with k embedded newlines into 1+k physical rows — the
 * "pasting from Google Sheets adds many extra rows" bug.
 *
 * Semantics (matching what GSheets/Excel emit):
 *  - a field is QUOTED iff its very first character (right after a delimiter
 *    or row start) is `"`;
 *  - inside quotes: `\t`, `\n`, `\r\n` are literal content; `""` decodes to `"`;
 *  - outside quotes: `\t` ends the field; `\r\n` | `\r` | `\n` ends the row;
 *  - a `"` in the middle of an unquoted field is literal content;
 *  - an unterminated quote consumes the rest of the input (Excel behavior).
 */
export function splitTsvIntoGrid(tsv: string): string[][] {
  const grid: string[][] = [];
  let row: string[] = [];
  let field = '';
  let fieldStarted = false;
  let inQuotes = false;
  let i = 0;

  const endField = () => {
    row.push(field);
    field = '';
    fieldStarted = false;
  };
  const endRow = () => {
    endField();
    grid.push(row);
    row = [];
  };

  while (i < tsv.length) {
    const ch = tsv[i];
    if (inQuotes) {
      if (ch === '"') {
        if (tsv[i + 1] === '"') {
          field += '"';
          i += 2;
        } else {
          inQuotes = false;
          i += 1;
        }
      } else {
        field += ch;
        i += 1;
      }
      continue;
    }
    if (ch === '"' && !fieldStarted) {
      inQuotes = true;
      fieldStarted = true;
      i += 1;
      continue;
    }
    if (ch === '\t') {
      endField();
      i += 1;
      continue;
    }
    if (ch === '\n' || ch === '\r') {
      endRow();
      i += ch === '\r' && tsv[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    field += ch;
    fieldStarted = true;
    i += 1;
  }
  // Flush the final row unless the input ended exactly on a row terminator
  // (a trailing newline must not emit a phantom empty row).
  if (field !== '' || fieldStarted || row.length > 0) endRow();
  return grid;
}

/** Build header + data grid for a bundle. `serializeCell` is the store's canonical
 *  cell serializer (arrays joined on their delimiter, JSON columns ordered). */
export function buildRowsTsvGrid(
  rows: TableRow[],
  columns: NodeColumnKey[],
  serializeCell: (row: TableRow, column: NodeColumnKey) => string
): string[][] {
  const header = columns.map((c) => COLUMN_DISPLAY_NAMES[c] ?? c);
  return [header, ...rows.map((r) => columns.map((c) => serializeCell(r, c)))];
}

const norm = (s: string) => s.trim().toLowerCase();

function buildHeaderLookup(
  columns: NodeColumnKey[]
): Map<string, NodeColumnKey> {
  const map = new Map<string, NodeColumnKey>();
  for (const key of columns) {
    map.set(norm(key), key); // raw enum key, e.g. "nodename"
    const display = COLUMN_DISPLAY_NAMES[key];
    if (display) map.set(norm(display), key); // display name, e.g. "node name"
  }
  return map;
}

/**
 * Parse a Google-Sheets / plain TSV paste into per-row cell maps keyed by node
 * column. If the first line's non-empty cells ALL resolve to a known column (by
 * display name or raw key, case-insensitive) it is treated as a header and used
 * for mapping; otherwise columns map POSITIONALLY over `fallbackColumns`.
 *
 * PSTUDIO-3611: uses `splitTsvIntoGrid` (RFC-4180-aware) so quoted cells that
 * contain embedded newlines or tabs are treated as ONE logical cell, not split
 * across multiple rows. This fixes "pasting from Google Sheets adds many extra
 * rows" when a Message column contains newlines.
 */
export function parseTsvToCellMaps(
  tsv: string,
  fallbackColumns: NodeColumnKey[]
): TsvParseResult {
  // PSTUDIO-3611: RFC-4180-aware split — quoted cells keep embedded
  // newlines/tabs (one LOGICAL row per grid row). Blank logical rows (every
  // cell trims to '') are dropped, mirroring the old blank-line filter.
  const rows = splitTsvIntoGrid(tsv).filter((cells) =>
    cells.some((c) => c.trim() !== '')
  );
  if (!rows.length) return { columns: [], cellMaps: [] };

  const lookup = buildHeaderLookup(fallbackColumns);
  const firstCells = rows[0];
  const resolvedFirst = firstCells.map((c) => lookup.get(norm(c)));
  let isHeader =
    firstCells.length > 0 &&
    resolvedFirst.every(
      (r, i) => r !== undefined || firstCells[i].trim() === ''
    );
  // A lone header-looking line with no data rows is more useful imported AS data
  // (e.g. a single Google-Sheets cell whose text equals a column label) than
  // silently swallowed into an empty result.
  if (isHeader && rows.length === 1) isHeader = false;

  // PSTUDIO-3337 (re-derived over LOGICAL rows — PSTUDIO-3611): a headerless
  // block where NO logical row has >= 2 cells is a free-text CELL value (e.g.
  // a copied Parameter Input JSON, single- or multi-line, quoted or not), NOT
  // a node grid. Importing it positionally would create one empty node per
  // line (the "~32 empty nodes" bug). A real node TSV always has >= 2 cells
  // on some row, and a recognized header always wins (checked above).
  if (!isHeader && !rows.some((cells) => cells.length >= 2)) {
    return { columns: [], cellMaps: [] };
  }

  let columns: Array<NodeColumnKey | undefined>;
  let dataRows: string[][];
  if (isHeader) {
    columns = resolvedFirst.map((r, i) => r ?? fallbackColumns[i]);
    dataRows = rows.slice(1);
  } else {
    columns = fallbackColumns.slice();
    dataRows = rows;
  }

  const cellMaps = dataRows.map((cells) => {
    const cellMap: Partial<Record<NodeColumnKey, string>> = {};
    cells.forEach((value, i) => {
      const col = columns[i];
      if (col) cellMap[col] = value;
    });
    return cellMap;
  });
  return { columns, cellMaps };
}

/**
 * Turn parsed TSV cell-maps into fresh node rows ready for pasting. Kept pure —
 * the store injects its column list, default/convert helpers, id factory and the
 * target `solutionVersionId` — so the mapping (every column defaulted, then the
 * known cells converted, a fresh id + current solution stamped, `settings`
 * defaulted) is unit-testable without dragging in the heavy store. Node
 * Orders are (re)assigned later by `pasteRow`; node numbers are preserved when free /
 * renumbered on collision (PSTUDIO-3574, guard rail 11).
 *
 * Note: a column whose `convert` returns `''` imports as empty. The TSV bridge
 * reuses the app's ONE canonical text→value converter instead of inventing a
 * second contract that could drift from it. (The store's converter used to answer
 * `''` for `command`; since PSTUDIO-4348 it keeps the text, and the store drops
 * it again where the row's node type clears the cell.)
 */
export function buildNodeRowsFromCellMaps(
  cellMaps: Array<Partial<Record<NodeColumnKey, string>>>,
  opts: {
    allColumns: NodeColumnKey[];
    getDefault: (column: NodeColumnKey) => unknown;
    convert: (text: string, column: NodeColumnKey) => unknown;
    solutionVersionId: string;
    makeId: () => string;
  }
): TableRow[] {
  return cellMaps.map((cellMap) => {
    const row: Record<string, unknown> = {};
    for (const col of opts.allColumns) row[col] = opts.getDefault(col);
    for (const col of Object.keys(cellMap) as NodeColumnKey[]) {
      row[col] = opts.convert(cellMap[col] ?? '', col);
    }
    row.id = opts.makeId();
    row.solutionVersionId = opts.solutionVersionId;
    row.settings = row.settings ?? {};
    return row as unknown as TableRow;
  });
}
